export type DatePrecision = "day" | "month" | "year";

export interface OriginDisplay {
	label: string;
	approx: boolean;
}

export type PromiseDateBaselineKind = "origin" | "recorded" | "none";

export interface PromiseDateBaseline {
	kind: PromiseDateBaselineKind;
	label: string;
	approx: boolean;
}

/** ISO date (YYYY-MM-DD) for `<input type="date" max>` — local calendar day. */
export function todayIsoDate(): string {
	const d = new Date();
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

/** YYYY-MM for `<input type="month" max>`. */
export function todayIsoMonth(): string {
	return todayIsoDate().slice(0, 7);
}

const MONTH_NAMES = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

export function formatOriginDate(
	isoDate: string,
	precision: DatePrecision,
): OriginDisplay {
	const d = new Date(isoDate);
	switch (precision) {
		case "day":
			return {
				label: `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`,
				approx: false,
			};
		case "month":
			return {
				label: `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}`,
				approx: true,
			};
		case "year":
			return { label: `${d.getUTCFullYear()}`, approx: true };
	}
}

export function formatRecordedDate(isoDate: string): string {
	const d = new Date(isoDate);
	return `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

export function formatPromiseDateBaseline(input: {
	originatedAt: string | null | undefined;
	createdAt: string | null | undefined;
	datePrecision: DatePrecision;
}): PromiseDateBaseline {
	if (input.originatedAt) {
		const { label, approx } = formatOriginDate(
			input.originatedAt,
			input.datePrecision,
		);
		return { kind: "origin", label, approx };
	}
	if (input.createdAt) {
		return {
			kind: "recorded",
			label: formatRecordedDate(input.createdAt),
			approx: false,
		};
	}
	return { kind: "none", label: "", approx: false };
}

/** Snap YYYY-MM-DD to precision (UTC), mirroring M17 coarsenDate. */
export function coarsenIsoDate(
	dayIso: string,
	precision: DatePrecision,
): string {
	if (!dayIso || dayIso.length < 10) return dayIso;
	const [y, m] = dayIso.slice(0, 10).split("-");
	switch (precision) {
		case "day":
			return dayIso.slice(0, 10);
		case "month":
			return `${y}-${m}-01`;
		case "year":
			return `${y}-01-01`;
	}
}

export function formatAnchorDetail(input: {
	kk: string | number;
	kkiVersion: string | null;
	country: string | null;
}): string {
	const kkNum = Number.parseFloat(String(input.kk));
	const kkLabel = Number.isFinite(kkNum) ? kkNum.toFixed(2) : String(input.kk);
	const parts = [`≈ ${kkLabel} KK`];
	if (input.kkiVersion) parts.push(input.kkiVersion);
	if (input.country) parts.push(input.country);
	return parts.join(" · ");
}

/** Day slice for correction date input seeding — only from explicit origin date. */
export function seedCorrectionOriginDate(
	originatedAt: string | null | undefined,
): string {
	return originDateInputValue(originatedAt ?? undefined);
}

/** Day slice for date inputs. */
export function originDateInputValue(isoDate: string | null | undefined): string {
	if (!isoDate) return "";
	return isoDate.slice(0, 10);
}

export function generateYearRange(maxYear?: number): number[] {
	const end = maxYear ?? new Date().getFullYear();
	const years: number[] = [];
	for (let y = end; y >= 1970; y--) {
		years.push(y);
	}
	return years;
}

/** Whether correction should include originated_at + date_precision. */
export function correctionDateChanged(input: {
	baselineOriginatedAt: string | null | undefined;
	baselineDatePrecision: DatePrecision;
	pickedDayIso: string;
	pickedPrecision: DatePrecision;
}): boolean {
	const pickedDay = input.pickedDayIso.trim();
	if (pickedDay === "") return false;

	const originalDay = input.baselineOriginatedAt
		? input.baselineOriginatedAt.slice(0, 10)
		: "";

	return (
		pickedDay !== originalDay ||
		input.pickedPrecision !== input.baselineDatePrecision
	);
}
